type IssuePromptFields = {
    promptTitle?: string | null;
    promptLabel?: string | null;
    promptDescription?: string | null;
};

export const formatReferencedComponent = (issue: IssuePromptFields): string => {
    const parts = [issue.promptTitle, issue.promptLabel, issue.promptDescription].filter(Boolean);
    return parts.join(' — ') || 'Referenced Component';
};

export const buildIssueStatusLabel = (status: string): string => {
    switch (status) {
        case 'inop':
            return 'Critical issue reported';
        case 'warn':
            return 'Warning reported';
        case 'under_review':
            return 'Issue under review';
        default:
            return 'Issue';
    }
};

export type IssueNoticeOrigin = 'Inspection' | 'Daily Service' | 'PM';

type IssueOriginFields = {
    promptLabel?: string | null;
    promptTitle?: string | null;
    log?: string | null;
};

export const issueNoticeOriginFromRecord = (issue: IssueOriginFields): IssueNoticeOrigin | null => {
    const label = issue.promptLabel || '';
    const title = issue.promptTitle || '';
    if (label === 'PM Inspection Issue' || title.startsWith('PM Issue')) {
        return 'PM';
    }

    try {
        const parsed = JSON.parse(issue.log || '[]');
        if (!Array.isArray(parsed)) return null;
        for (const entry of parsed) {
            const source = typeof entry?.source === 'string' ? entry.source : '';
            if (source === 'InspectionReport' || source === 'Inspection') return 'Inspection';
            if (source === 'DailyServiceReport' || source === 'Daily Service') return 'Daily Service';
            if (source === 'PM') return 'PM';
        }
    } catch {
        return null;
    }

    return null;
};

export const buildIssueDescription = (status: string): string => {
    switch (status) {
        case 'inop':
            return 'Equipment marked out of service.';
        case 'warn':
        case 'Open':
            return 'Issue flagged during service.';
        case 'under_review':
            return 'Issue is under review.';
        default:
            return 'Issue reported.';
    }
};

export const getIssueSubtitleClassName = (status: string): string | undefined => {
    switch (status) {
        case 'inop':
            return 'error';
        case 'warn':
            return 'warning';
        case 'under_review':
            return 'info';
        default:
            return undefined;
    }
};

export const buildIssuePageTitle = (status: string, equipmentName?: string | null): string => {
    const equipment = equipmentName || 'Unknown Equipment';
    return `${buildIssueStatusLabel(status)} on ${equipment}`;
};

export const ISSUES_TAB_QUERY = {
    param: 'tab',
    archived: 'archived',
} as const;

export type IssuesListTab = 'current' | 'archived';

export const parseIssuesListTab = (searchParams: URLSearchParams): IssuesListTab =>
    searchParams.get(ISSUES_TAB_QUERY.param) === ISSUES_TAB_QUERY.archived ? 'archived' : 'current';

export const buildIssuesListPath = (tab: IssuesListTab = 'current'): string => {
    if (tab === 'archived') {
        return `/issues?${ISSUES_TAB_QUERY.param}=${ISSUES_TAB_QUERY.archived}`;
    }
    return '/issues';
};

type IssueEquipmentLookup = {
    units?: Array<{ id?: string | null } | null> | null;
};

/** Case-insensitive Unit Name / ID match against the unit catalog. */
export const filterIssuesByUnitName = <T extends { unitId?: string | null }>(
    issues: T[],
    query: string,
    unitNameById: ReadonlyMap<string, string>,
): T[] => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) {
        return issues;
    }

    return issues.filter((issue) => {
        if (!issue.unitId) {
            return false;
        }

        const unitName = unitNameById.get(issue.unitId) ?? '';
        return unitName.toLowerCase().includes(normalizedQuery);
    });
};

/** Unit detail route when the issue is tied to a unit (or category has a single unit). */
export const buildIssueEquipmentDetailPath = (
    equipmentId: string,
    unitId: string | null | undefined,
    equipmentById?: Record<string, IssueEquipmentLookup | null | undefined>,
): string | null => {
    if (unitId) {
        return `/equipment/${unitId}`;
    }
    const units = equipmentById?.[equipmentId]?.units?.filter((unit) => unit?.id) ?? [];
    if (units.length === 1 && units[0]?.id) {
        return `/equipment/${units[0].id}`;
    }
    return null;
};
